import { ensureClient } from './util';
import { Tables } from '@/types/supabase';

export type LeaderboardRow = {
  playerId: string;
  firstName: string;
  lastName: string;
  username?: string | null;
  currentRating: number;
  matchesPlayed: number;
  clubName?: string | null;
  winRate: number;
  currentStreak: string;
  ratingChange: number;
};

export const leaderboardModel = {
  async getFederationsForCurrentUser() {
    const client = ensureClient();
    type Fed = {
      id: string;
      name: string;
      slug: string | null;
      requires_furigana: boolean;
      default_currency: string | null;
    };
    const { data, error: sessionErr } = await client.auth.getSession();
    if (sessionErr) throw sessionErr;
    const session = data.session;
    if (!session) return [] as Fed[];
    const userId = session.user.id;
    if (!userId) return [] as Fed[];

    const { data: memberships, error: memErr } = await client
      .from('federation_user')
      .select('federation_id')
      .eq('user_id', userId);
    if (memErr) throw memErr;
    const federationIds = (memberships ?? []).map((m) => m.federation_id);
    if (federationIds.length === 0) return [] as Fed[];

    const { data: federations, error: fedErr } = await client
      .from('federation')
      .select('id, name, slug, requires_furigana, default_currency')
      .in('id', federationIds);
    if (fedErr) throw fedErr;
    return (federations ?? []) as Fed[];
  },

  async getFederationById(federationId: string) {
    const client = ensureClient();
    const { data, error } = await client
      .from('federation')
      .select('id, name, default_currency')
      .eq('id', federationId)
      .maybeSingle();
    if (error) throw error;
    return data as { id: string; name: string; default_currency: string | null } | null;
  },
  async getOrganizationAndSportForCurrentUser() {
    const client = ensureClient();
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    const session = data.session;
    if (!session) return { org: null, sportId: null } as const;
    const userId = session.user.id;
    if (!userId) {
      return { org: null as null | { id: string; name: string }, sportId: null as null | string };
    }

    // Fallback: schema does not yet include organizations/sports in generated types
    // Return nulls so the UI shows "no rankings" until backed by real data sources
    return { org: null, sportId: null } as const;
  },

  async getRowsForOrg(_orgId: string, _sportId: string): Promise<LeaderboardRow[]> {
    // Fallback: schema does not yet include the referenced views/tables; return an empty list
    return [];
  },

  async getPlayersForFederation(federationId: string): Promise<Tables<'player'>[]> {
    const client = ensureClient();

    // Only federation_user members: by login, or by player for a member whose login was deleted
    type Mem = { user_id: string | null; player_id: string | null };
    const { data: memberships, error: memErr } = await client
      .from('federation_user')
      .select('user_id, player_id')
      .eq('federation_id', federationId);
    if (memErr) throw memErr;
    const rows = (memberships ?? []) as Mem[];
    const userIds = rows.flatMap((m) => (m.user_id ? [m.user_id] : []));
    const loginlessPlayerIds = rows.flatMap((m) => (!m.user_id && m.player_id ? [m.player_id] : []));
    if (userIds.length === 0 && loginlessPlayerIds.length === 0) return [];

    const byId = new Map<string, Tables<'player'>>();
    if (userIds.length > 0) {
      const { data: players, error: playersErr } = await client
        .from('player')
        .select('*')
        .in('user_id', userIds);
      if (playersErr) throw playersErr;
      for (const p of (players ?? []) as Tables<'player'>[]) byId.set(p.id, p);
    }
    if (loginlessPlayerIds.length > 0) {
      const { data: players, error: playersErr } = await client
        .from('player')
        .select('*')
        .in('id', loginlessPlayerIds);
      if (playersErr) throw playersErr;
      for (const p of (players ?? []) as Tables<'player'>[]) byId.set(p.id, p);
    }

    return [...byId.values()];
  },

  // Most efficient approach: Use RPC with SQL JOINs
  async getRowsForFederation(federationId: string): Promise<LeaderboardRow[]> {
    const client = ensureClient();
    const { data, error } = await client.rpc('get_federation_leaderboard', {
      federation_id: federationId,
    });

    if (error) {
      throw new Error(`Failed to fetch leaderboard: ${error.message}`);
    }

    return data.map((row) => ({
      playerId: row.player_id,
      firstName: row.first_name,
      lastName: row.last_name,
      username: row.username,
      currentRating: row.current_rating ?? 0,
      matchesPlayed: row.matches_played ?? 0,
      clubName: row.club_name,
      winRate: row.win_rate ?? 0,
      currentStreak: row.current_streak ?? '0W',
      ratingChange: row.rating_change ?? 0,
    }));
  },
};
